import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {EmailApiService} from '../api/email-api.service';
import {PaymentApiService, PaymentOutcome} from '../api/payment-api.service';
import {UserApiService} from '../api/user-api.service';
import {User} from '../models/user.model';

/**
 * Where Stripe sends the payer after checkout. This page never decides that a payment succeeded: it asks the server
 * to re-check the session with Stripe and shows that verdict. The server also confirms the registration / membership
 * on its own (webhook + background sweeper), so nothing here is required for the payment to count — a payer who
 * closes this tab is still handled.
 */
@Component({
    selector: 'app-payment-success',
    templateUrl: './payment-success.component.html',
    styleUrl: './payment-success.component.css'
})
export class PaymentSuccessComponent implements OnInit {
    sessionId: string | null = null;
    pageid = 1;
    /** What the template shows while/after verifying. */
    state: 'verifying' | 'paid' | 'processing' | 'not-completed' | 'unverified' = 'verifying';
    outcome: PaymentOutcome | null = null;

    private readonly userRecentId: string = sessionStorage.getItem('userId') || '';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private paymentApi: PaymentApiService,
        private userApiService: UserApiService,
        private emailApi: EmailApiService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.sessionId = this.route.snapshot.queryParamMap.get('session_id');
        this.pageid = Number(this.route.snapshot.queryParamMap.get('pageid'));

        if (!this.sessionId) {
            this.toastr.error('Payment session missing.', 'Error');
            this.router.navigate(['/']);
            return;
        }

        this.paymentApi.syncSession(this.sessionId).subscribe({
            next: (outcome) => {
                this.outcome = outcome;
                if (outcome.paid) {
                    this.state = 'paid';
                    this.onPaid(outcome);
                } else if (outcome.status === 'PROCESSING') {
                    this.state = 'processing';
                    this.leaveAfterNotice();
                } else {
                    this.state = 'not-completed';
                    this.leaveAfterNotice();
                }
            },
            error: (err) => {
                // Stripe or the server could not be reached. The payment is not lost: the server re-checks it on its own.
                console.error('Could not verify payment', err);
                this.state = 'unverified';
                this.leaveAfterNotice();
            }
        });
    }

    // -----------------------------------------------------

    private onPaid(outcome: PaymentOutcome): void {
        const amount = outcome.amount ? (Number(outcome.amount) / 100).toFixed(2) : '';
        const money = amount + ' ' + (outcome.currency || '').toUpperCase();
        const reference = outcome.reference || this.sessionId || '';

        switch (this.pageid) {
            case 1: { // new membership
                const user = this.readJson('pendingRegistration');
                this.sendEmail(user && {
                    name: user.firstname + ' ' + user.lastname, email: user.email, phone: user.phone,
                    subject: 'New Membership Registration',
                    message: 'New Membership Registration\nPayment: ' + outcome.status + '\nTransaction ID: ' + reference + '\nAmount: ' + money
                });
                this.clear('pendingRegistration', 'pendingMembershipType', 'pendingBizConference', 'profileData');
                this.toastr.success('Payment Successful! You can now log in.', 'Success');
                this.router.navigate(['/login']);
                break;
            }
            case 3: { // existing member paying from the profile page
                const user = this.readJson('profileData');
                this.sendEmail(user && {
                    name: user.firstname + ' ' + user.lastname, email: user.email, phone: user.phone,
                    subject: 'Existing Membership Registration',
                    message: 'Existing Membership Registration\nPayment: ' + outcome.status + '\nTransaction ID: ' + reference + '\nAmount: ' + money
                });
                this.clear('profileData');
                this.toastr.success('Payment Successful!', 'Success');
                this.router.navigate(['/profile']);
                break;
            }
            case 2: { // business conference
                const biz = this.readJson('pendingBizConference');
                this.sendEmail(biz && {
                    name: biz.firstName + ' ' + biz.lastName, email: biz.email, phone: biz.phone,
                    subject: 'Business 2026 Conference Registration',
                    message: 'Business 2026 Conference Registration\nPayment: ' + outcome.status + '\nTransaction ID: ' + reference + '\nAmount: ' + money
                });
                this.clear('pendingBizConference');
                this.toastr.success('Payment Successful! Your registration is confirmed.', 'Success');
                this.router.navigate(['/biz-conf']);
                break;
            }
            case 4: { // event registration / booking
                const eventId = sessionStorage.getItem('eventId') || outcome.eventId;
                const returnPath = sessionStorage.getItem('eventReturnPath');
                if (this.userRecentId) {
                    this.userApiService.getShortData(this.userRecentId).subscribe({
                        next: (data) => {
                            const user = data as User;
                            this.sendEmail({
                                name: user.firstname + ' ' + user.lastname, email: user.email,
                                subject: 'Event Registration',
                                message: 'Event Registration\nPayment: ' + outcome.status + '\nTransaction ID: ' + reference + '\nAmount: ' + money
                            });
                        },
                        error: (err) => console.error('Could not load user details for confirmation email', err)
                    });
                }
                this.clear('eventId', 'eventRegistration', 'eventBooking', 'eventReturnPath');
                this.toastr.success('Payment Successful!', 'Success');
                if (returnPath) {
                    this.router.navigateByUrl(returnPath);
                } else if (eventId) {
                    this.router.navigate(['/event-home/' + eventId]);
                } else {
                    this.router.navigate(['/profile']);
                }
                break;
            }
            default:
                this.router.navigate(['/']);
        }
    }

    /** Payment is still being confirmed / did not complete: tell the payer, then send them back where they came from. */
    private leaveAfterNotice(): void {
        setTimeout(() => {
            if (this.pageid === 4) {
                const returnPath = sessionStorage.getItem('eventReturnPath');
                const eventId = sessionStorage.getItem('eventId') || this.outcome?.eventId;
                if (returnPath) { this.router.navigateByUrl(returnPath); return; }
                if (eventId) { this.router.navigate(['/event-home/' + eventId]); return; }
                this.router.navigate(['/profile']);
            } else if (this.pageid === 2) {
                this.router.navigate(['/biz-conf']);
            } else if (this.pageid === 3) {
                this.router.navigate(['/profile']);
            } else {
                this.router.navigate(['/login']);
            }
        }, 6000);
    }

    private readJson(key: string): any {
        try {
            const raw = sessionStorage.getItem(key);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    private clear(...keys: string[]): void {
        keys.forEach(k => sessionStorage.removeItem(k));
    }

    /** One confirmation email per Stripe session, even if the payer refreshes this page. */
    private sendEmail(payload: any): void {
        if (!payload || !payload.email || !this.sessionId) return;
        const key = 'paymentEmailSent_' + this.sessionId;
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
        this.emailApi.sendRegistrationEmail(payload).subscribe({
            error: (err) => console.error('Confirmation email failed to send', err)
        });
    }
}
